import { useNotification } from 'admin';
import { CompanyTypeEnum, Country, isFieldErrorResponse, useApiTradeV1TradeCompaniesCreate } from 'api';
import {
  CompanyTypeSelectField,
  CountryAutocompleteField,
  Form,
  type InferType,
  TextField,
  useForm,
  yup,
  yupResolver,
} from 'form';
import { useNavigate } from 'react-router-dom';
import { Button } from 'ui';

import { getAuthRoute } from '../../utils/getAuthRoute';

const schema = yup.object().shape({
  company_type: yup.string().oneOf(Object.values(CompanyTypeEnum)).required('Company type is required'),
  country: yup.string().oneOf(Object.values(Country)).required('Country is required'),
  name: yup.string().required('Name is required'),
});

type FormValues = InferType<typeof schema>;
export const CompanySignUpForm = () => {
  const navigate = useNavigate();
  const { open } = useNotification();
  const { mutate, isPending } = useApiTradeV1TradeCompaniesCreate({
    mutation: {
      onError(error) {
        if (isFieldErrorResponse(error)) {
          Object.keys(error.response?.data).forEach((field) => {
            form.setError(field as keyof FormValues, { message: error.response.data[field] });
          });
          if (error.response?.data.non_field_errors) {
            open?.({
              description: error?.response?.data.non_field_errors[0],
              message: 'Error signing up',
              type: 'error',
            });
          }
          return;
        }

        open?.({
          message: 'Error signing up',
          type: 'error',
        });
      },
      onSuccess() {
        navigate(getAuthRoute('/sign-up/success'));
      },
    },
  });

  const form = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: FormValues) => {
    mutate({
      data,
    });
  };

  return (
    <Form {...form} onSubmit={onSubmit} data-testid="company-sign-up-form">
      <TextField type="text" name="name" size="medium" label="Name" required autoFocus fullWidth />
      <CountryAutocompleteField name="country" size="medium" label="Country" required fullWidth />
      <CompanyTypeSelectField name="company_type" size="medium" label="Company type" required />
      <Button type="submit" size="large" loading={isPending}>
        Next
      </Button>
    </Form>
  );
};
